import type { JSONContent } from '@tiptap/core'

import type { ArgumentsType } from '../abstracts'
import { AnyObject } from '../utils'
import type { TypeInstanceMap } from './data'
import type { FieldCellValue } from './field'
import type { FilterFormType } from './filter'
import type { Sorter } from './general'
import type { NodeFieldContent, RichTextContentProtocol } from './richtext'
import type { VariableADTvalue } from './variable'

export enum FlowSelectSourceType {
    UPSTREAM = 'UPSTREAM',
    DATASOURCE = 'DATASOURCE'
}

export type SourceRecordConfig = {
    selectType: FlowSelectSourceType
    nodeId: string
    dataSourceId: string
    filter?: FilterFormType
    sorts?: Sorter[]
    fields?: NodeFieldContent[]
}

export type NodeArg = {
    id: string
    name: string
    innerType: ArgumentsType
    value?: JSONContent
}

export type NodeResolvedArg = {
    id: string
    name: string
    innerType: ArgumentsType
    value?: FieldCellValue
}

export type SubProcessArg = NodeArg

export type EndArg = NodeArg

export type SubProcessResolvedArg = NodeResolvedArg

export type EndResolvedArg = NodeResolvedArg

/** Merchant configuration is optional in drafts and required before execution. */
export type WechatMerchantConfig = {
    paymentAccountId?: string
}

export type WechatPayConfig = SourceRecordConfig &
    WechatMerchantConfig & {
        /** 商品描述 */
        descFieldId: string
        /** 订单金额 */
        amountFieldId: string
        /** 是否为分账订单 */
        profitSharingFieldId?: string
        /** 支付结束时间 */
        orderTimeExpire?: string
        // 跳转回原页面
        autoRedirectAfterPayment: boolean
        /** 成功后设置的更新订单 */
        succeedFieldSettings: { fieldId: string; value: AnyObject }[]
        callbackWorkflow: {
            workflowId: string // 绑定的子流程ID,
            workflowArgs: SubProcessArg[]
        }
    }

export type WechatRefundConfig = SourceRecordConfig &
    WechatMerchantConfig & {
        /** 付款订单号 */
        payOrderFieldId: string
        /** 付款订单金额 */
        payAmountFieldId: string
        /** 退款金额 */
        refundAmountFieldId: string
        /** 退款原因 */
        reasonFieldId: string
    }

export type WechatRefundQueryConfig = WechatMerchantConfig & {
    /** 退款订单号 */
    refundOrderId: RichTextContentProtocol
}

/** 订单单号类型：1 商户单号，2 微信支付单号 */
export type WechatUploadShippingInfoConfig = WechatMerchantConfig & {
    orderNumberType: number
    transactionId?: RichTextContentProtocol
    outTradeNo?: RichTextContentProtocol
    openid?: RichTextContentProtocol
    /** 物流模式：1 快递 2 同城 3 虚拟 4 自提 */
    logisticsType: number
    /** 发货模式：1 统一发货 2 分拆发货 */
    deliveryMode: number
    isAllDelivered?: RichTextContentProtocol
    trackingNo?: RichTextContentProtocol
    expressCompany?: RichTextContentProtocol
    itemDesc?: RichTextContentProtocol
    receiverContact?: RichTextContentProtocol
    consignorContact?: RichTextContentProtocol
    shippingListNodeId?: string
}

export type WechatGetOrderShippingConfig = WechatMerchantConfig & {
    orderNumberType: number
    transactionId?: RichTextContentProtocol
    merchantTradeNo?: RichTextContentProtocol
}

export type WechatGetOrderListConfig = {
    openid?: RichTextContentProtocol
    /** 订单状态：1 待发货 … 6 资金待结算，空表示不筛选 */
    orderState?: number
    payTimeBegin?: RichTextContentProtocol
    payTimeEnd?: RichTextContentProtocol
    lastIndex?: RichTextContentProtocol
    pageSize?: RichTextContentProtocol
}

export type WechatFollowWaybillConfig = {
    openid?: RichTextContentProtocol
    waybillId?: RichTextContentProtocol
    transId?: RichTextContentProtocol
    receiverPhone?: RichTextContentProtocol
    senderPhone?: RichTextContentProtocol
    orderDetailPath?: RichTextContentProtocol
    deliveryId?: RichTextContentProtocol
    goodsName?: RichTextContentProtocol
    goodsImgUrl?: RichTextContentProtocol
    goodsDesc?: RichTextContentProtocol
    goodsListNodeId?: string
}

export enum WechatProfitSharingPlatformEnum {
    WECHAT = 'WECHAT',
    MINI_PROGRAM = 'MINI_PROGRAM'
}

export enum WechatProfitSharingTypeEnum {
    MERCHANT_ID = 'MERCHANT_ID',
    PERSONAL_OPENID = 'PERSONAL_OPENID'
}

export enum WechatProfitSharingRelationTypeEnum {
    STORE = 'STORE',
    STAFF = 'STAFF',
    STORE_OWNER = 'STORE_OWNER',
    PARTNER = 'PARTNER',
    HEADQUARTER = 'HEADQUARTER',
    BRAND = 'BRAND',
    DISTRIBUTOR = 'DISTRIBUTOR',
    USER = 'USER',
    SUPPLIER = 'SUPPLIER',
    CUSTOM = 'CUSTOM'
}

// 请求分账
export type WechatProfitSharingConfig = WechatMerchantConfig & {
    outTradeNo: RichTextContentProtocol
    profitSharingOrderId: RichTextContentProtocol
    // 是否解冻剩余未分资金
    isUnfreezeRemainingFund: RichTextContentProtocol
    // 分账接收方列表
    profitSharingReceiver: {
        nodeId: string
        profitSharingAccount: RichTextContentProtocol
        profitSharingPlatform: WechatProfitSharingPlatformEnum
        profitSharingType: WechatProfitSharingTypeEnum
        profitSharingAmount: RichTextContentProtocol
        profitSharingDescription: RichTextContentProtocol
    }
}

export type WechatProfitSharingQueryConfig = WechatMerchantConfig & {
    // 商户分账单号
    profitSharingOrderId: RichTextContentProtocol
    // 微信订单号
    outTradeNo: RichTextContentProtocol
}

export type WechatProfitSharingQueryAmountConfig = WechatMerchantConfig & {
    outTradeNo: RichTextContentProtocol
}

export type WechatTemplateMsgConfig = {
    personList: VariableADTvalue[]
    templateId: string
    values: { variableName: string; value: RichTextContentProtocol }[]
    url: RichTextContentProtocol
}

export type StripePayConfig = SourceRecordConfig & {
    // 商品描述
    descFieldId: string
    // 货币类型
    currency: string
    /** 订单金额 */
    amountFieldId: string
    autoRedirectAfterPayment: boolean
    // 商品名称
    productNameFieldId: string
    // 商品图片
    productPictureFieldId: string
    succeedFieldSettings: { fieldId: string; value: AnyObject }[]
    callbackWorkflow: {
        workflowId: string // 绑定的子流程ID,
        workflowArgs: SubProcessArg[]
    }
}

export type WechatSubscriptionMsgNotificationConfig = {
    personList: VariableADTvalue[]
    templateId: string
    values: { variableName: string; value: VariableADTvalue }[]
    page: {
        openPageId: string
        pathParameters?: { id: string; value?: RichTextContentProtocol }[]
        queryParameters?: { id: string; value?: RichTextContentProtocol }[]
    }
}

export type OpenWechatCustomerServiceConfig = {
    // 企业微信corpId
    corpId: RichTextContentProtocol
    // 客服链接url
    url: RichTextContentProtocol
}

export type WechatNavigateToMiniProgramConfig = {
    appId: RichTextContentProtocol
    path: RichTextContentProtocol
}

export type WechatOpenChannelsActivityConfig = {
    finderUserName: RichTextContentProtocol
    feedId: RichTextContentProtocol
}
